<template>
  <div>
    <PageTitle title="User Roles" />
    <div class="table-wrapper mt-6">
      <v-row class="table-controls mb-1 items-center">
        <v-col sm="6" md="6" lg="6">
          <v-btn
            color='transparent'
            depressed
            @click='$router.push({name: "UsersRolesAdd"})'
            class='px-2'
          >
            <v-img max-height="14" max-width="14" class="mr-2" src="@/assets/icons/svg/plus.svg" />
            <span class="text-subtitle-2 font-weight-bold">Add New Role</span>
          </v-btn>
        </v-col>
        <v-col sm="6" md="6" lg="6">
          <div class="d-flex justify-end">
            <v-btn
              v-show='haveSelected'
              @click='deleteMulty=true'
              color='transparent'
              depressed
            >
              <v-img max-height="20" max-width="20" class="mr-2" src="@/assets/icons/svg/delete1.svg" />
              <span class="text-subtitle-2 font-weight-bold">Delete</span>
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-divider />
      <v-row class="justify-space-between">
        <v-col cols="12" class="px-0">
          <v-data-table
            :headers="headers"
            :items="getRolesList"
            :page.sync="page"
            :items-per-page="itemsPerPage"
            item-key="created"
            @page-count="pageCount = $event"
            class="cursor-point relative-position"
            hide-default-footer
          >
            <template
              v-if="itemIsLoading"
              v-slot:body
            >
              <tbody>
                <div class="justify-center flex flex-center flex-column full-width">
                  <circle-progress class="mt-16" />
                </div>
              </tbody>
            </template>
            <template :slot='"no-results"'>
              <p class="text-h6 my-16">No Data found</p>
            </template>
            <template :slot='"no-data"'>
              <p class="text-h6 my-16">No Data found</p>
            </template>
            <template v-slot:[`item.selected`]="{ item }">
              <v-simple-checkbox
                v-if='!item.system'
                v-model="item.selected"
                color="#7972c0"
                :ripple="false"
              ></v-simple-checkbox>
            </template>
            <template v-slot:[`item.rights`]='{ item }'>
              <div class='mb-1 mt-3'>
                <span class='font-weight-bold'>Main:</span>
                <div class='mt-1'>
                  <rights-chip
                    :item="buildRightsData(item, 'Warehouses', 'warehouse')"
                  />
                  <rights-chip
                    :item="buildRightsData(item, 'Items', 'item')"
                  />
                  <rights-chip
                    :item="buildRightsData(item, 'Folders', 'category')"
                  />
                  <rights-chip
                    :item="buildRightsData(item, 'Custom Fields', 'customField')"
                  />
                </div>
              </div>
              <div class='mt-1 mb-3'>
                <span class='font-weight-bold'>Access Rights:</span>
                <div class='mt-1'>
                  <access-chip
                    :item="buildAccessData(item, 'import', 'Inventory import', 'inventoryImport')"
                  />
                  <access-chip
                    :item="buildAccessData(item, 'export', 'Inventory export', 'inventoryExport')"
                  />
                  <access-chip
                    :item="buildAccessData(item, 'reports', 'Reports', 'reports')"
                  />
                  <access-chip
                    :item="buildAccessData(item, 'alerts', 'Alerts management', 'alertManagement')"
                  />
                  <access-chip
                    :item="buildAccessData(item, 'roles', 'Role management', 'roleManagement')"
                  />
                  <access-chip
                    :item="buildAccessData(item, 'history', 'Activity history', 'activityHistory')"
                  />
                  <access-chip
                    :item="buildAccessData(item, 'users', 'User management', 'userManagement')"
                  />
                  <access-chip
                    :item="buildAccessData(item, 'integrations', 'Integration management', 'integrationManagement')"
                  />
                </div>
              </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-row class="flex-nowrap">
                <v-col cols="4" class="px-0">
                  <v-tooltip top transition="fade-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <img
                        class="mr-1 cursor-pointer"
                        style="height: 16px; width: 16px"
                        src="@/assets/icons/svg/draw.svg"
                        @click='$router.push({ name: "UsersRolesEditing", params: { id: item.id } })'
                        v-bind="attrs"
                        v-on="on"
                        title="Edit"
                      />
                    </template>
                    <span>edit role</span>
                  </v-tooltip>
                </v-col>
                <v-col cols="4" class="px-0" v-if='!item.system'>
                  <v-tooltip top transition="fade-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <img
                        style="height: 16px; width: 16px"
                        src="@/assets/icons/svg/delete1.svg"
                        class="cursor-pointer"
                        @click='deleteSingle = item'
                        v-bind="attrs"
                        v-on="on"
                        title="Delete"
                      />
                    </template>
                    <span>delete role</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row class="pagination-wrapper">
        <v-col cols="5">
          <span class="text-nowrap mr-2">Items per page</span>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                text
                outlined
                v-bind="attrs"
                v-on="on"
                style="padding: 0; font-weight: 600"
              >
                {{ itemsPerPage }}
                <v-icon>mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(item, index) in itemsPerPageOptions"
                :key="index"
                link
                @click="itemsPerPage = item"
              >
                <v-list-item-title>{{ item }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
        <v-col cols="7" class="d-flex justify-end">
          <v-pagination
            v-model="page"
            :length="pageCount"
            :total-visible="5"
            color="#7972C0"
          ></v-pagination>
        </v-col>
      </v-row>
    </div>
    <delete-multy
      v-if='deleteMulty'
      @doDelete='handleMultyDelete'
      @cancel='deleteMulty=false'
    />
    <delete-single
      v-if='deleteSingle'
      :role='deleteSingle.role'
      :usersCount='deleteSingle.usersCount'
      @doDelete='handleSingleDelete'
      @cancel='deleteSingle=null'
    />
  </div>
</template>

<script>
import PageTitle from '@/components/page/PageTitle'
import CustomCircleProgressBar from '@/components/CustomCircleProgressBar'
import { mapGetters } from 'vuex'
import deleteMulty from '@/components/roles/popups/deleteMulty'
import deleteSingle from '@/components/roles/popups/deleteSingle'
import deleteRolesMixin from '@/components/roles/delete/_deleteMixin'
import rightsChip from '@/components/roles/rightsChips/rightsChip'
import accessChip from '@/components/roles/rightsChips/accessChip'
export default {
  name: 'UsersRolesList',
  components: {
    PageTitle,
    'circle-progress': CustomCircleProgressBar,
    deleteMulty,
    deleteSingle,
    rightsChip,
    accessChip
  },
  mixins: [deleteRolesMixin],
  data: function() {
    return {
      navSettingsOptions: [
        {
          index: 1,
          text: 'Settings'
        },
        {
          index: 2,
          text: 'User Roles'
        }
      ],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      itemsPerPageOptions: [5, 10, 20, 25, 50],
      headers: [
        {
          text: '',
          align: 'start',
          sortable: false,
          value: 'selected',
          width: 50
        },
        {
          text: 'Role Name',
          value: 'name',
          sortable: true,
          align: 'start',
          width: 160
        },
        {
          text: 'Users',
          value: 'usersCount',
          sortable: true,
          align: 'start',
          width: 100
        },
        {
          text: 'Rights',
          value: 'rights',
          sortable: false,
          align: 'start'
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'end',
          width: 100
        }
      ],
      deleteMulty: false,
      deleteSingle: null,
      itemIsLoading: true
    }
  },
  computed: {
    ...mapGetters('roles', ['getRolesList']),
    selectedList() {
      return this.getRolesList.filter(item => item.selected)
    },
    haveSelected() {
      return !!this.selectedList.length
    }
  },
  watch: {
    getRolesList(v) {
      if (v.length) this.itemIsLoading = false
    }
  },
  methods: {
    async handleMultyDelete() {
      const result = await this.doDelete(this.selectedList)
      if (result.result) {
        this.$store.dispatch('roles/deleteRole', this.selectedList)
        this.deleteMulty = false
      } else {
        this.$store.dispatch('showSubmitFormErrorPopup', {
          title: 'Error delete role',
          message: result.message
        })
      }
    },
    async handleSingleDelete() {
      const result = await this.doDelete([this.deleteSingle])
      if (result.result) {
        this.$store.dispatch('roles/deleteRole', [this.deleteSingle])
        this.deleteSingle = false
      } else {
        this.$store.dispatch('showSubmitFormErrorPopup', {
          title: 'Error delete role',
          message: result.message
        })
      }
    },
    buildRightsData(item, name, key) {
      const result = { name: name, rights: [] }
      result.rights.push({ name: 'view', value: item.rights[key + 'View'] })
      result.rights.push({ name: 'create', value: item.rights[key + 'Create'] })
      result.rights.push({ name: 'edit', value: item.rights[key + 'Edit'] })
      result.rights.push({ name: 'delete', value: item.rights[key + 'Delete'] })
      return result
    },
    buildAccessData(item, name, description, key) {
      const result = { name: name, description: description }
      result.value = item.rights[key]
      return result
    }
  },
  mounted() {
    if (this.getRolesList.length) this.itemIsLoading = false
  }
}
</script>

<style lang='scss'>
  .delete-button {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    outline: none;
    padding: 5px;
    background-color: transparent;
    transition: 0.2s linear;
    border-radius: 3px;
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
</style>
